import { type ReactNode } from 'react'
import { Box, Typography, CircularProgress, IconButton, Tooltip } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import { BORDER_BLACK, FS_SM, ICON_XS } from '../common/theme'
import { hasAnswerText } from '../common/common'
import { useQuestionProcessing, clearQuestionProcessingLog } from '../services/TaskManagerService'
import { useAppActions } from '../AppContext'
import { DataTable, type DataTableColumn } from './DataTable'
import TaPadlock from './TaPadlock'
import TaWordCount from './TaWordCount'
import TraceArc from './TraceArc'
import ScoreArc from './ScoreArc'
import GenerateResponseButton from './buttons/GenerateResponseButton'
import GenerateTraceButton from './buttons/GenerateTraceButton'
import GenerateScoringButton from './buttons/GenerateScoringButton'
import type { Question, QuestionLocks } from '../types/question'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'


export type WorkflowNavTarget = 'sources' | 'storyboard' | 'answer' | 'trace' | 'score'

interface BidQuestionPlanTabProps {
  question: Question
  isStreaming: boolean
  tracing?: boolean
  scoring?: boolean
  generating?: boolean
  // Click on a row's Task label jumps to that step's panel in the parent
  // (left tab Sources / Storyboard / Score, or the right-pane Answer /
  // Trace sub-tab). When omitted the labels render as plain text.
  onNavigate?: (target: WorkflowNavTarget) => void
}

// Lock keys that the workflow rows expose. The header padlock acts as
// a "lock all" master across this set — clicking it flips every row to
// the opposite state.
const LOCKABLE_KEYS: (keyof QuestionLocks)[] = ['question', 'sources', 'structure', 'answer', 'scoring']

// One row per workflow step. `done` and `active` drive the status icon;
// `detail` is the rightmost column (a TaWordCount / TraceArc / ScoreArc
// per row, depending on the step); `action` is the inline generate /
// regenerate button when the parent supplies a handler. `navTarget`
// identifies which BidQuestion panel the Task label links to.
interface StepRow {
  key: string
  label: string
  done: boolean
  processing: boolean
  detail: ReactNode
  navTarget: WorkflowNavTarget
  // Pre-rendered action button for the row's Action column. Each row
  // drops in its own self-contained button (GenerateResponseButton etc.)
  // - they own their own running/disabled state via the processing
  // registry, so the row doesn't need to thread click handlers here.
  actionButton?: ReactNode
  // Which question-locks field this row toggles. Null for rows that don't
  // own a lock (Trace), so the lock column renders an empty placeholder.
  lockKey: keyof QuestionLocks | null
}

export default function BidQuestionPlanTab({
  question,
  onNavigate,
}: BidQuestionPlanTabProps) {

  const { updateQuestion } = useAppActions()
  const processing = useQuestionProcessing(question.questionsha)

  const locks = question.locks

  // Merge a lock patch onto the question's `locks` map. Single
  // mutator point so individual rows and the master header padlock
  // both flow through here.
  const applyLockPatch = (patch: Partial<QuestionLocks>) => {
    updateQuestion(question.questionsha, {
      locks: { ...(question.locks ?? {} as any), ...patch },
    })
  }

  // Master padlock state. The header cell shows locked only when
  // every row-level lock is engaged; clicking it flips them all the
  // opposite way (any-unlocked → lock all; all-locked → unlock all).
  const allLocked = LOCKABLE_KEYS.every(k => !!locks?.[k])
  const toggleAllLocks = () => {
    const next = !allLocked
    const patch: Partial<QuestionLocks> = {}
    for (const k of LOCKABLE_KEYS) patch[k] = next
    applyLockPatch(patch)
  }
  const sourcesCount = question.sourcedocs?.length ?? 0
  const sourcesDone = sourcesCount > 0
  const structureDone = !!question.structure
  const answerDone = hasAnswerText(question)
  // `simint`/`scoreint` use `>= 0` as the "generated" gate — `-1`
  // (or undefined) is the server's "not run yet" sentinel, while a
  // real `0` is a valid traced / scored result that should light the
  // workflow checkmark green. Same convention used by the toolbar
  // ScoreArc / TraceArc dots.
  const rawTraceSim = (question.answerobj as any)?.simint
  const traceSim: number | null = typeof rawTraceSim === 'number' && rawTraceSim >= 0 ? rawTraceSim : null
  const traceDone = traceSim != null
  const rawScoreVal = (question.bideval as any)?.scoreint
  const scoreVal: number | null = typeof rawScoreVal === 'number' && rawScoreVal >= 0 ? rawScoreVal : null
  const scoreDone = scoreVal != null

  const log = processing?.log ?? []

  const rows: StepRow[] = [
    {
      key: 'sources',
      label: 'Sources',
      done: sourcesDone,
      processing: processing.sourcedocs,
      navTarget: 'sources',
      lockKey: 'sources',
      detail: (
        <Typography variant="caption" sx={{ fontSize: FS_SM, opacity: sourcesDone ? 0.85 : 0.4, fontVariantNumeric: 'tabular-nums' }}>
          {sourcesCount === 0 ? '-' : `${sourcesCount} source${sourcesCount === 1 ? '' : 's'}`}
        </Typography>
      ),
    },
    {
      key: 'structure',
      label: 'Storyboard',
      done: structureDone,
      processing: processing.structure,
      navTarget: 'storyboard',
      lockKey: 'structure',
      detail: structureDone ? (
        <TaWordCount
          text={question.structure as any}
          question={question}
        />
      ) : <Typography variant="caption" sx={{ fontSize: FS_SM, opacity: 0.4 }}>-</Typography>,
    },
    {
      key: 'answer',
      label: 'Answer',
      done: answerDone,
      processing: processing.answer,
      navTarget: 'answer',
      lockKey: 'answer',
      detail: answerDone ? (
        <TaWordCount
          structure={question.answerobj?.answerstructure}
          question={question}
          replylimit={question.replylimit}
        />
      ) : <Typography variant="caption" sx={{ fontSize: FS_SM, opacity: 0.4 }}>-</Typography>,
      actionButton: <GenerateResponseButton question={question} />,
    },
    {
      key: 'trace',
      label: 'Trace',
      done: traceDone,
      processing: processing.isprocessingtrace,
      navTarget: 'trace',
      lockKey: null,
      detail: <TraceArc value={traceSim} />,
      actionButton: <GenerateTraceButton question={question} />,
    },
    {
      key: 'score',
      label: 'Estimated Score',
      done: scoreDone,
      processing: processing.isprocessingscoring,
      navTarget: 'score',
      lockKey: 'scoring',
      detail: <ScoreArc value={scoreVal} />,
      actionButton: <GenerateScoringButton question={question} />,
    },
  ]

  const cols: DataTableColumn<StepRow>[] = [

    {
      id: 'label', label: 'Task', width: 140, sortable: false,
      render: (row) => (
        <Typography
          variant="caption"
          onClick={onNavigate ? () => onNavigate(row.navTarget) : undefined}
          sx={{
            fontSize: FS_SM,
            opacity: row.done || row.processing ? 1 : 0.55,
            ...(onNavigate ? {
              cursor: 'pointer',
              '&:hover': { color: 'primary.main', textDecoration: 'underline' },
            } : null),
          }}
        >
          {row.label}
        </Typography>
      ),
    },
    {
      id: 'status', label: 'Done', width: 50, align: 'center', sortable: false, iconColumn: false,
      render: (row) => row.processing ? (
        <CircularProgress size={14} thickness={5} />
      ) : row.done ? (
        <CheckCircleOutlineIcon sx={{ fontSize: ICON_XS, color: 'success.main' }} />
      ) : (
        <RadioButtonUncheckedIcon sx={{ fontSize: ICON_XS, opacity: 0.3 }} />
      ),
    },
    {
      id: 'action', label: 'Action', width: 65, align: 'center', sortable: false, iconColumn: false,
      // Self-contained per-row buttons own their own running / disabled
      // / spinner state via the processing registry, so the column just
      // drops in whatever the row supplies (or a spacer).
      render: (row) => row.actionButton ?? <Box sx={{ width: 28 }} />,
    },
    {
      id: 'lock', width: 50, align: 'center', sortable: false, iconColumn: false,
      // Master padlock for the column — locks / unlocks every row's
      // section flag at once. Shows locked only when all rows are
      // already locked.
      headerRender: () => (
        <TaPadlock
          isLocked={allLocked}
          onToggle={toggleAllLocks}
        />
      ),
      render: (row) => row.lockKey ? (
        <TaPadlock
          isLocked={!!locks?.[row.lockKey]}
          onToggle={() => applyLockPatch({ [row.lockKey!]: !locks?.[row.lockKey!] })}
        />
      ) : <Box sx={{ width: 28 }} />,
    },
    {
      id: 'detail', label: 'Detail', width: 0, flex: 1, minWidth: 0, align: 'right', sortable: false,
      // Detail cell is clickable just like the Task label - jumps to the
      // corresponding panel via the parent's onNavigate callback.
      render: (row) => (
        <Box
          onClick={onNavigate ? () => onNavigate(row.navTarget) : undefined}
          sx={onNavigate ? {
            display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end',
            cursor: 'pointer',
            '&:hover': { color: 'primary.main' },
          } : undefined}
        >
          {row.detail}
        </Box>
      ),
    },
  ]

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>

      {/* Workflow datatable - top of the panel, natural height. Doesn't
          scroll; everything below it (activity / chat) lives in its own
          flex region that absorbs the rest of the height. */}
      <Box sx={{ p: 1, flexShrink: 0 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: FS_SM, mb: 1, display: 'block', px: 0.5 }}>
          Question Workflow
        </Typography>
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
          <DataTable<StepRow>
            rows={rows}
            cols={cols}
            rowKey={(r) => r.key}
            testIdPrefix="pmq_workflow"
          />
        </Box>
      </Box>

      {/* Scrollable middle - activity timeline first (directly under the
          datatable), then the streamed assistant reply when present. */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1 }}>

        {/* Event timeline - every non-chunk taskstream event */}
        {log.length > 0 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: FS_SM, opacity: 0.7 }}>
                Activity
              </Typography>
              <Tooltip title="Clear activity">
                <IconButton
                  size="small"
                  data-testid="pmq_workflow_clear_activity_btn"
                  onClick={() => clearQuestionProcessingLog(question.questionsha)}
                  sx={{ p: 0.25, ml: 'auto' }}
                >
                  <CancelOutlinedIcon sx={{ fontSize: ICON_XS }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              {log.map((entry, i) => {
                const isLast = i === log.length - 1
                return (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.45, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                      {new Date(entry.ts).toLocaleTimeString([], { hour12: false })}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: FS_SM, opacity: isLast ? 1 : 0.85, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {entry.text}
                    </Typography>
                  </Box>
                )
              })}
            </Box>
          </Box>
        )}
      </Box>
    </Box >
  )
}
